import React from "react";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {useNavigate, useParams } from "react-router";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Settings, Settings2, X } from "lucide-react";
import MigrationStatusChip from "../components/common/MigrationStatusChip";
import FirmDocumentsFoldersRepository from "../repositories/FirmDocumentsFoldersRepository";
import {FirmDocumentsFolderWithStatistics} from "../models/FirmDocumentFolder/FirmDocumentsFolder";
import {FirmDocument} from "../models/FirmDocumentFolder/FirmDocument";
import {UserRole} from "../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../components/common/ActionConfirmationModal";
import ConfirmationModal from "../components/common/ConfirmationModal";
import {QUERY_CONFIG} from "../constants/QueryConfig";
import {QUERY_KEYS} from "../constants/QueryKeys";
import {FirmDocumentsFolderEmptyFoldersTable} from "../components/FirmDocumentsFolderEmptyFolders/FirmDocumentsFolderEmptyFoldersTable";
import {FirmDocumentsTable, FirmDocumentsTableRef} from "../components/FirmDocuments/FirmDocumentsTable";
import {ConflictResolveView} from "../components/common/Table/File/ConflictResolveView";
import {CREATE_DELETE_FILES_REQUEST_TABLE_CONFIGS, CreateDeleteFilesRequestTable} from "../components/common/File/CreateDeleteFilesRequestTable";
import { DeleteFirmDocumentsStatusTable } from "../components/FirmDocuments/DeleteFirmDocumentsStatusTable";
import {ExportFirmDocumentsToCsvStatusTable} from "../components/FirmDocuments/ExportFirmDocumentsToCsvStatusTable";
import {ActiveJobsExplorer} from "../models/Jobs/ActiveJobsExplorer";
import MigrationEntityShortStatistics from "../components/common/MigrationEntity/MigrationEntityShortStatistics";
import { useManagementDrawer } from "../hooks/useManagementDrawer";
import { useDetailBreadcrumb } from "../contexts/DetailBreadcrumbContext";
import { FlaggedPathResolveView } from "../components/common/Table/File/FlaggedPathResolveView";
import { MigrationType } from "../models/Statistics/MigrationType";
import { useRequiresReason } from "../hooks/useRequiresReason";

interface FirmDocumentsFolderPageProps {
  userRole: UserRole
  isAssignedUser: boolean;
}

const FirmDocumentsFolderPage: React.FC<FirmDocumentsFolderPageProps> = ({
  userRole,
  isAssignedUser
}: FirmDocumentsFolderPageProps) => {
  const requiresReason = useRequiresReason();
  const { organizationId, firmDocumentsFolderId } = useParams();
  const navigate = useNavigate();
  const firmDocumentFoldersRepository = new FirmDocumentsFoldersRepository();
  const firmDocumentsTableRef = useRef<FirmDocumentsTableRef>(null);
  const [firmDocumentFolderEntity, setFirmDocumentsFolder] = useState<FirmDocumentsFolderWithStatistics | undefined>(undefined);
  const [newFirmDocumentsForDeletion, setNewFirmDocumentsForDeletion] = useState<FirmDocument[]>([]);
  const [selectedTab, setSelectedTab] = useState<ActiveJobsExplorer>(ActiveJobsExplorer.Export);
  const [viewMode, setViewMode] = useState<"files" | "folders">("files");
  const [isConfirmExportModalOpen, setIsConfirmExportModalOpen] = React.useState<boolean>(false);
  const queuedCount = newFirmDocumentsForDeletion.length;
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(queuedCount);
  const { setEntityLabel } = useDetailBreadcrumb();

  const firmDocumentFolderQuery = useQuery({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDER, firmDocumentsFolderId], queryFn:
    async () => await firmDocumentFoldersRepository.getFirmDocumentsFolder(firmDocumentsFolderId ?? ""), gcTime: QUERY_CONFIG.DEFAULT_MIGRATION_ENTITY_CACHE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_MIGRATION_ENTITY_REFETCH_INTERVAL,
      refetchOnMount: true, });

  useEffect(() => {
    if (firmDocumentFolderQuery.data) {
      setFirmDocumentsFolder(firmDocumentFolderQuery.data);
      setEntityLabel(firmDocumentFolderQuery.data.folderPath);
    }
    return () => setEntityLabel(null);
  }, [firmDocumentFolderQuery.data, firmDocumentsFolderId]);

  if (firmDocumentFolderQuery.isLoading || (!firmDocumentFolderQuery.isError && !firmDocumentFolderEntity))
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-4 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-96" />
        <Skeleton className="h-[400px] w-full rounded-md" />
      </div>
    );

  if (firmDocumentFolderQuery.isError)
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Failed to load firm documents folder.</p>
        <Button variant="outline" size="sm" onClick={() => firmDocumentFolderQuery.refetch()}>
          Retry
        </Button>
      </div>
    );

  if (!firmDocumentFolderEntity) return null;

  const startCurrentViewExportToCsv = (reason?: string) =>
    firmDocumentsTableRef.current?.exportCurrentViewToCsv(reason);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="w-full my-2 gap-2 flex-wrap shrink-0">
        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-start">
            <h2 className="text-3xl font-bold tracking-tight">
              Firm Documents Folder
              <MigrationStatusChip
                status={firmDocumentFolderEntity.status}
                className="ml-[14px]"
              />
            </h2>
            <div className="flex flex-row gap-2">
              {userRole !== UserRole.Viewer && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDrawerOpen(!drawerOpen)}
                  className="gap-2"
                >
                  <Settings2 className="size-4" />
                  Manage
                  {queuedCount > 0 && (
                    <Badge variant="secondary" className="ml-1 px-1.5">
                      {queuedCount}
                    </Badge>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/organization/${organizationId}/firm-documents-folders`)}
              >
                Back
              </Button>
            </div>
          </div>
          <p className="text-base mb-1">
            {firmDocumentFolderEntity.folderPath}
          </p>
          <div className="flex flex-row w-full flex-1 justify-between items-center gap-2">
            <MigrationEntityShortStatistics
              totalNumberOfFiles={firmDocumentFolderEntity.statistics.totalNumberOfFiles ?? 0}
              filesMigrated={firmDocumentFolderEntity.statistics.filesMigrated ?? 0}
              filesFlagged={firmDocumentFolderEntity.statistics.filesFlagged ?? 0}
              filesBlockedByEntityFlag={firmDocumentFolderEntity.statistics.filesInEntityBlockedByUser ?? 0}
              filesInterruptedInitial={firmDocumentFolderEntity.statistics.filesInterruptedInitial ?? 0}
              filesInterruptedDelta={firmDocumentFolderEntity.statistics.filesInterruptedDelta ?? 0}
              filesModified={firmDocumentFolderEntity.statistics.filesModified ?? 0}
              filesNotFound={firmDocumentFolderEntity.statistics.filesNotFound ?? 0}
              filesNotFoundMarkedForRetry={firmDocumentFolderEntity.statistics.filesNotFoundMarkedForRetry ?? 0}
              unresolvedConflictCount={firmDocumentFolderEntity.statistics.unresolvedConflictCount ?? 0}
              acknowledgedConflictCount={firmDocumentFolderEntity.statistics.acknowledgedConflictCount ?? 0}
              filesSizeInMb={firmDocumentFolderEntity.statistics.filesSizeInMb ?? 0}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          {viewMode === "files" ? (
            <FirmDocumentsTable
              ref={firmDocumentsTableRef}
              organizationId={organizationId ?? ""}
              firmDocumentsFolderId={firmDocumentsFolderId ?? ""}
              activeJobsExplorer={selectedTab}
              newFirmDocumentsForDeletion={newFirmDocumentsForDeletion}
              setNewFirmDocumentsForDeletion={setNewFirmDocumentsForDeletion}
              disabled={userRole === UserRole.Viewer}
              toolbar={
                <div className="flex w-full items-center gap-2 my-1">
                  <ToggleGroup type="single" value={viewMode} onValueChange={(v) => { if (v) setViewMode(v as "files" | "folders"); }} variant="outline" size="sm" className="flex-1">
                    <ToggleGroupItem value="files" className="flex-1">Files</ToggleGroupItem>
                    <ToggleGroupItem value="folders" className="flex-1">Empty folders</ToggleGroupItem>
                  </ToggleGroup>
                  {firmDocumentFolderEntity.hasFlaggedPaths && (
                    <FlaggedPathResolveView
                      organizationId={organizationId ?? ""}
                      migrationEntityId={firmDocumentFolderEntity.folderPath}
                      migrationType={MigrationType.FirmDocumentsMigration}
                      entityNoun="folder"
                      invalidateQueryKeys={[[QUERY_KEYS.FIRM_DOCUMENTS_FOLDER]]}
                    />
                  )}
                  {userRole !== UserRole.Viewer && (
                    <ConflictResolveView
                      parentId={firmDocumentsFolderId ?? ""}
                      entityNoun="firm documents folder"
                      groupsQueryKey={[QUERY_KEYS.FIRM_DOCUMENTS_CONFLICT_GROUPS, firmDocumentsFolderId]}
                      invalidateQueryKeys={[[QUERY_KEYS.FIRM_DOCUMENTS], [QUERY_KEYS.FIRM_DOCUMENTS_FOLDER, firmDocumentsFolderId]]}
                      fetchGroups={(id, includeResolved) => firmDocumentFoldersRepository.getConflictGroups(id, includeResolved)}
                      resolveGroup={(resolutions) => firmDocumentFoldersRepository.resolveConflictGroup(resolutions)}
                    />
                  )}
                </div>
              }
            />
          ) : (
            <FirmDocumentsFolderEmptyFoldersTable
              firmDocumentsFolderId={firmDocumentsFolderId ?? ""}
              toolbar={
                <ToggleGroup type="single" value={viewMode} onValueChange={(v) => { if (v) setViewMode(v as "files" | "folders"); }} variant="outline" size="sm" className="w-full my-1">
                  <ToggleGroupItem value="files" className="flex-1">Files</ToggleGroupItem>
                  <ToggleGroupItem value="folders" className="flex-1">Empty folders</ToggleGroupItem>
                </ToggleGroup>
              }
            />
          )}
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage files</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 p-4">
              <ToggleGroup
                type="single"
                value={String(selectedTab)}
                onValueChange={(v) => { if (v) setSelectedTab(Number(v) as ActiveJobsExplorer); }}
                variant="outline"
                size="sm"
                className="w-full my-1"
              >
                {(userRole === UserRole.Admin || isAssignedUser) && (
                  <ToggleGroupItem value={String(ActiveJobsExplorer.Delete)} className="flex-1">Delete files</ToggleGroupItem>
                )}
                <ToggleGroupItem value={String(ActiveJobsExplorer.Export)} className="flex-1">Export CSV</ToggleGroupItem>
              </ToggleGroup>
              <div className="flex-1 overflow-auto min-h-0">
                {selectedTab === ActiveJobsExplorer.Delete && (userRole === UserRole.Admin || isAssignedUser) && (
                  <div className="flex flex-col h-full gap-4">
                    {newFirmDocumentsForDeletion.length > 0 && (
                      <>
                        <div className="flex items-center gap-3 py-2 pt-4">
                          <Separator className="flex-1" />
                          <span className="text-xs font-medium text-muted-foreground"><b>New</b></span>
                          <Separator className="flex-1" />
                        </div>
                        <CreateDeleteFilesRequestTable
                          organizationId={organizationId ?? ""}
                          entityId={firmDocumentsFolderId ?? ""}
                          entityKey={firmDocumentFolderEntity.folderPath}
                          newFilesForDeletion={newFirmDocumentsForDeletion}
                          setNewFilesForDeletion={setNewFirmDocumentsForDeletion}
                          fileTypeConfig={CREATE_DELETE_FILES_REQUEST_TABLE_CONFIGS.FIRM_DOCUMENTS_CONFIG}
                        />
                        <div className="flex items-center gap-3 py-2">
                          <Separator className="flex-1" />
                          <span className="text-xs font-medium text-muted-foreground"><b>Deletion status</b></span>
                          <Separator className="flex-1" />
                        </div>
                      </>
                    )}
                    <DeleteFirmDocumentsStatusTable firmDocumentsFolderId={firmDocumentsFolderId ?? ""} />
                  </div>
                )}
                {selectedTab === ActiveJobsExplorer.Export && (
                  <div className="flex flex-col h-full gap-4">
                    <div className="pt-4 w-full h-full">
                      {requiresReason ? (
                        <ActionConfirmationModal
                          actionConfig={ACTION_CONFIGS.EXPORT_FIRM_DOCUMENTS_VIEW}
                          onConfirm={async (reason) => {
                            startCurrentViewExportToCsv(reason);
                          }}
                        >
                          <Button>Export current files view</Button>
                        </ActionConfirmationModal>
                      ) : (
                        <Button onClick={() => setIsConfirmExportModalOpen(true)}>
                          Export current files view
                        </Button>
                      )}
                      <ExportFirmDocumentsToCsvStatusTable
                        firmDocumentsFolderId={firmDocumentsFolderId ?? ""}
                        userRole={userRole}
                        isAssignedUser={isAssignedUser}
                        organizationId={organizationId ?? ""}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {isConfirmExportModalOpen && (
        <ConfirmationModal
          open={isConfirmExportModalOpen}
          onClose={() => {setIsConfirmExportModalOpen(false);}}
          onConfirm={async () => {
            startCurrentViewExportToCsv();
            setIsConfirmExportModalOpen(false);
          }}
          message="Are you sure you want to export firm documents from the current view?"
          statusColor="primary"
          buttonText="Confirm"
        />
      )}
    </div>
  );
};

export default FirmDocumentsFolderPage;
